import {
  BranchQuoteSupportItemType,
  BranchQuoteSupportStatus,
  QuoteItemConversation,
  ViewBranchQuoteSupportInboxItem
} from '../../../api/dealer-api-interface-franchisee';
import {
  buildModifiedTime,
  buildTimeBetween,
  getSupportStatusBadge,
  getSupportTypeDisplayValue
} from '../../conversation/conversation-helper';
import { cache } from '../../cache-impl/cache-registry';
import { customElement, state } from 'lit/decorators.js';

import { EventSnippet } from '../../../../webmodule-common/interop/webmodule-interop';
import { formatQuoteNumber } from '../../../quotes/data/quote-helper-functions';
import { getApiFactory } from '../../../api/api-injector';
import { html, TemplateResult } from 'lit';
import { DataTableWrapper } from '../../../../webmodule-common/other/ui/datatable-view';
import type { TabPage } from '../../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { RequestPage, ResultPaginated } from '../../../../webmodule-common/other/ui/RequestPage';
import { resolveURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import { resourceQuote } from '../../../quotes/ui/launcher';
import {
  serverDateTimeToLocal,
  serverDateTimeToLocalDateTime
} from '../../../../webmodule-common/other/datetime-converter';
import { LangQuoteSupportInboxTable } from '../../../../localization/scripts/dealer-franchisee/support-inbox/views/quote-support-inbox-table-lang';
import { userDataStore } from '../../common/current-user-data-store';
import {
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../../webmodule-common/components/src/webmodule-components';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid';
import { DataTracker, FieldType } from '../../../../webmodule-common/other/ui/databinding/data-tracker';
import { DataBinding } from '../../../../webmodule-common/other/ui/databinding/databinding';
import { FormInputAssistant } from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant';

export interface QuoteSupportInboxTableOptions {
  tabName: string;
  caption: EventSnippet;
  statusFilter: BranchQuoteSupportStatus;
  typeFilter: BranchQuoteSupportItemType;
  getBranchUsers: () => string[];
  columnOptions?: QuoteSupportInboxColumnOptions;
  defaultUserId?: string;
}

export interface QuoteSupportInboxColumnOptions {
  showResolvedColumns?: boolean;
  hideLastModifiedColumn?: boolean;
  hideRequestTypeColumn?: boolean;
}

@customElement('wm-branchquotesupportinboxviewtab')
export class QuoteSupportInboxTable extends DataTableWrapper<ViewBranchQuoteSupportInboxItem> {
  franchiseeApi = getApiFactory().franchisee();
  userCache = cache().userProfile;

  options: QuoteSupportInboxTableOptions;

  dataIsStale = false;
  private titleFilter: string | null = null;

  private dataTracker: DataTracker;

  @state()
  private _conversationLinks: QuoteItemConversation[] | undefined;
  private get conversationLinks(): QuoteItemConversation[] | undefined {
    return this._conversationLinks;
  }

  private set conversationLinks(value: QuoteItemConversation[] | undefined) {
    this._conversationLinks = value;
  }

  @state() _selectedTicketOwnerId?: string;
  private get selectedTicketOwnerId(): string | undefined {
    return this._selectedTicketOwnerId;
  }

  private set selectedTicketOwnerId(value: string | undefined) {
    this._selectedTicketOwnerId = value;
    this.resetPageAndRefresh();
  }

  constructor(options: QuoteSupportInboxTableOptions) {
    super();
    this.options = options;

    this.selectedTicketOwnerId = options.defaultUserId ?? emptyGuid;

    this.dataTracker = new DataTracker(new DataBinding(this));

    const addField = (
      fieldName: string,
      propertyType?: FieldType,
      nullable?: boolean,
      editorFieldName?: string,
      data?: () => any
    ) => {
      this.dataTracker.addObjectBinding(
        data ?? (() => this),
        fieldName,
        editorFieldName ?? fieldName,
        propertyType ?? FieldType.string,
        nullable ?? false
      );
    };
    addField('selectedTicketOwnerId', FieldType.string, false);
  }

  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<ViewBranchQuoteSupportInboxItem>> {
    const result = await this.franchiseeApi.getBranchQuoteSupportInbox({
      filter: this.titleFilter,
      requestPage: {
        pageIndex: request.pageIndex,
        pageSize: request.pageSize
      },
      sortAsc: request.sortAsc,
      sortField: request.sortField,
      statusFilter: this.options.statusFilter,
      ticketOwnerId: this.selectedTicketOwnerId ?? emptyGuid,
      typeFilter: this.options.typeFilter,
      branchId: userDataStore.defaultBranch.id
    });
    if (result) {
      this.conversationLinks = result.conversationLinks;
      return result.items;
    }
    this.conversationLinks = [];
    return {
      count: 0,
      pageCount: 0,
      pageIndex: 0,
      pageSize: this.pageLength(),
      results: []
    };
  }

  getColumns(): WebModuleTableColumnDef[] {
    // Column sizing moves from Bootstrap `colpx-*` / `colpxmax-*` classes (which
    // don't pierce the new table's shadow DOM) to the column's own width model:
    // fixed widths (had `colpxmax-*`) become `width`, the text columns that should
    // absorb slack keep a `minWidth` floor and grow.
    const columns: WebModuleTableColumnDef[] = [];

    columns.push({
      title: () => LangQuoteSupportInboxTable.quoteNumber(),
      fieldName: 'quoteNumber',
      sortable: true,
      width: '120px',
      fade: true,
      displayValue: (_table: WebModuleTable, item: ViewBranchQuoteSupportInboxItem) => {
        const quoteNumber = formatQuoteNumber(item.quoteSuffix, item.quoteNumber, item.quoteType);
        return this.getQuoteLink(item, quoteNumber);
      }
    });
    columns.push({
      title: () => LangQuoteSupportInboxTable.quoteTitle(),
      fieldName: 'title',
      minWidth: '200px',
      fade: true,
      displayValue: (_table: WebModuleTable, item: ViewBranchQuoteSupportInboxItem) => {
        return this.getQuoteLink(item, item.quoteTitle);
      }
    });
    columns.push({
      title: () => LangQuoteSupportInboxTable.client(),
      fieldName: 'clientName',
      minWidth: '160px',
      fade: true,
      displayValue: (_table: WebModuleTable, item: ViewBranchQuoteSupportInboxItem) => {
        return `${item.clientName}`;
      }
    });
    columns.push({
      title: () => LangQuoteSupportInboxTable.ticketTitle(),
      fieldName: 'ticketTitle',
      minWidth: '160px',
      fade: true,
      displayValue: (_table: WebModuleTable, item: ViewBranchQuoteSupportInboxItem) => {
        const hasConversation =
          item.requestType === BranchQuoteSupportItemType.EngineeredToOrder ||
          item.requestType === BranchQuoteSupportItemType.QuoteAssistanceRequest;
        return hasConversation ? this.getConversationLink(item, item.subject) : item.subject;
      }
    });
    columns.push({
      title: () => LangQuoteSupportInboxTable.author(),
      fieldName: 'createdUserId',
      width: '160px',
      fade: true,
      displayValue: (_table: WebModuleTable, item: ViewBranchQuoteSupportInboxItem) => {
        const author = this.userCache.getLocal(item.createdUserId);
        return `${author?.data.friendlyName ?? ''}`;
      }
    });
    if (!this.options.columnOptions?.hideRequestTypeColumn) {
      columns.push({
        title: () => LangQuoteSupportInboxTable.requestType(),
        fieldName: 'requestType',
        sortable: true,
        width: '150px',
        displayValue: (_table: WebModuleTable, item: ViewBranchQuoteSupportInboxItem) => {
          return getSupportTypeDisplayValue(item.requestType);
        }
      });
    }
    columns.push({
      title: () => LangQuoteSupportInboxTable.status(),
      fieldName: 'status',
      sortable: true,
      width: '120px',
      displayValue: (_table: WebModuleTable, item: ViewBranchQuoteSupportInboxItem) => {
        return getSupportStatusBadge(item.status);
      }
    });
    columns.push({
      title: () => LangQuoteSupportInboxTable.dateSubmitted(),
      fieldName: 'dateCreated',
      sortable: true,
      width: '160px',
      displayValue: (_table: WebModuleTable, item: ViewBranchQuoteSupportInboxItem) => {
        return serverDateTimeToLocalDateTime(item.dateCreated).toLocaleString();
      }
    });
    if (!this.options.columnOptions?.hideLastModifiedColumn) {
      columns.push({
        title: () => LangQuoteSupportInboxTable.lastModified(),
        fieldName: 'lastModified',
        sortable: true,
        minWidth: '210px',
        isDefaultSortDirectionAscending: false,
        displayValue: (_table: WebModuleTable, item: ViewBranchQuoteSupportInboxItem) => {
          const label = cache().userProfile.getLocalData(item.lastModifiedUserId)?.friendlyName ?? '';
          const dateValue = buildModifiedTime(serverDateTimeToLocalDateTime(item.lastModified));
          // Elapsed time on the first line, the user on its own line in italics.
          // Wrap both in one block so they stack vertically inside the flex-row cell.
          return html`<div class="lastmodified-cell">
            <div class="lastmodified-date">${dateValue}</div>
            <div class="lastmodified-user"><em>${label}</em></div>
          </div>`;
        }
      });
    } else if (this.options.columnOptions?.showResolvedColumns) {
      columns.push({
        title: () => LangQuoteSupportInboxTable.dateResolved(),
        fieldName: 'lastModified',
        sortable: true,
        width: '140px',
        displayValue: (_table: WebModuleTable, item: ViewBranchQuoteSupportInboxItem) => {
          const resolvedDate =
            item.status === BranchQuoteSupportStatus.Resolved
              ? serverDateTimeToLocal(item.lastModified).toLocaleDateString()
              : '-';

          return resolvedDate;
        }
      });
      columns.push({
        title: () => LangQuoteSupportInboxTable.resolution(),
        fieldName: 'resolution',
        width: '120px',
        displayValue: (_table: WebModuleTable, item: ViewBranchQuoteSupportInboxItem) => {
          const resolvedDate =
            item.status === BranchQuoteSupportStatus.Resolved
              ? buildTimeBetween(
                  serverDateTimeToLocalDateTime(item.dateCreated),
                  serverDateTimeToLocalDateTime(item.lastModified)
                )
              : '-';
          return resolvedDate;
        }
      });
    }

    return columns;
  }

  getQuoteLink(item: ViewBranchQuoteSupportInboxItem, value: string | TemplateResult): TemplateResult {
    return html`<a
      class="quote-link"
      href="${resolveURL(resourceQuote, item.branchQuoteId)}"
      quoteid="${item.branchQuoteId}"
      >${value}</a
    >`;
  }

  getConversationLink(item: ViewBranchQuoteSupportInboxItem, value: string | TemplateResult): TemplateResult {
    const supportFragment =
      item.requestType === BranchQuoteSupportItemType.EngineeredToOrder ||
      item.requestType === BranchQuoteSupportItemType.QuoteAssistanceRequest
        ? `support_${item.conversationId}`
        : null;
    return html`<a
      class="quote-link"
      href="${resolveURL(resourceQuote, item.branchQuoteId, supportFragment)}"
      quoteid="${item.branchQuoteId}"
      >${value}</a
    >`;
  }

  override enableFiltering(): boolean {
    return true;
  }

  override updateFilter(_searchTerm: string | null) {
    // The wrapper's filter input calls this then re-queries from page 1; just store the term.
    this.titleFilter = _searchTerm;
  }

  override enableAdvancedFiltering(): boolean {
    return true;
  }

  override getDefaultSortField(): string | undefined {
    if (!this.options.columnOptions?.hideLastModifiedColumn) {
      return 'lastModified';
    }
    if (this.options.columnOptions?.showResolvedColumns) {
      return 'lastModified';
    }
    return 'quoteNumber';
  }

  override advancedFilterTemplate(): TemplateResult {
    const forms = new FormInputAssistant(this.dataTracker, false, true);
    const uniqueTicketOwners = [...new Set(this.options.getBranchUsers())];
    uniqueTicketOwners.unshift(emptyGuid);

    const toSelectValue = (text: string, value: string) => {
      return { text: text, value: value, disabled: false };
    };

    const convertAuthorId = (authorId: string) => {
      if (authorId === emptyGuid) {
        return toSelectValue(`All Users`, authorId);
      }
      const entry = this.userCache.getLocal(authorId);
      if (entry?.data) {
        return toSelectValue(entry.data.friendlyName ?? entry.displayValue, entry.data.id);
      }
      return toSelectValue(``, authorId);
    };

    return html`
      ${forms.arraySelect('selectedTicketOwnerId', uniqueTicketOwners, convertAuthorId, {
        title: LangQuoteSupportInboxTable.ticketOwner()
      })}
    `;
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  override async rowClicked(
    _details: CustomEvent<{ table: WebModuleTable; item: ViewBranchQuoteSupportInboxItem }>
  ): Promise<void> {
    // Legacy open-ticket event removed; navigation handles deep linking.
  }

  // The inbox tabs are a fixed-height region; let the table own the vertical
  // scroll so a second (tab) scrollbar does not appear beside it. The fill-height
  // flex chain for the wrapper is set up in dealer_ui.scss. (Pagination summary,
  // page size and rows-per-page selector come from the DataTableWrapper defaults.)
  override fillHeight(): boolean {
    return true;
  }

  getConversationLinks(conversationId: string) {
    return this.conversationLinks?.filter(x => x.conversationId === conversationId) ?? [];
  }

  public createTabPage(): TabPage {
    return {
      caption: () => this.options.caption(),
      content: () => this,
      onEnter: () => {
        //enable data load the first time we enter the tab, but not before
        this.delayedDataLoad = false;
        // not-awaiting on purpose, for faster page switch. safe to do
        if (this.dataIsStale) this.refreshData();
      },
      data: this,
      fragment: this.options.tabName
    };
  }
}
